import React from 'react';
import { isEmbeddableVideo } from '../utils/journey';
import 'react-medium-image-zoom/dist/styles.css';

import { Nullable } from 'src/types/nullable.type';
import { Content } from './interfaces/content.interface';
import { isMicrosoftOfficeType } from 'src/utils/mime';

import EmbeddableVideoPlayer from '../dashboard/EmbeddableVideoPlayer';
import JourneyExternalIntegration from '../dashboard/JourneyExternalIntegration';
import JourneyPDF from '../dashboard/JourneyPDF';
import { ZoomableImage } from './ZoomableImage';
import ExternalLinkCard from 'src/common/components/external-link';
import { Node } from './interfaces/node.interface';
import { Journey } from './interfaces/journey.interface';
import { DocumentViewerFactory } from 'src/document-editor/viewer.factory';
import { ViewMicrosoftFile } from './ViewMicrosoftFile';

interface JourneyViewContentProps {
  content: Content | Node;
  journey?: Nullable<Journey>;
  email?: string;
  canEdit: boolean;
  autoplay?: boolean;
  brandingColor: string;
  galleryMode?: boolean;
}

const JourneyViewContent = ({
  content,
  email,
  canEdit,
  autoplay = false,
  brandingColor,
  galleryMode,
  ...restProps
}: JourneyViewContentProps) => {
  if (!content) {
    return null;
  }

  const { id, name, content_type, embed, file_asset, image_asset, external_link, document } = content;

  const renderEmbeddableVideo = () => {
    return <EmbeddableVideoPlayer currentNode={content} {...restProps} />;
  };

  const renderIntegration = () => {
    return <JourneyExternalIntegration id={id} embed={embed} {...restProps} email={email || ''} />;
  };

  const renderPDF = () => {
    return <JourneyPDF {...content} data={(content && 'data' in content) ? content.data : undefined} {...restProps} />;
  };

  const renderImage = () => {
    if (!image_asset) return null;

    const { url } = image_asset;

    return (
      <ZoomableImage key={url} url={url} containerClassName='flex flex-1 max-w-full max-h-full image-content-wrapper' />
    );
  };

  const renderExternalLink = () => {
    const link = {
      ...external_link,
      title: external_link?.title || name,
    };
    return <ExternalLinkCard isThumbnailHidden={false} link={link} {...restProps} disabled={galleryMode} />;
  };

  const renderFile = () => {
    if (!file_asset) return null;

    const {
      id: file_asset_id,
      url,
      content_type,
      name: link_name,
      data,
    } = file_asset;

    const { link_title, link_description, hide_image } = data || {};

    const link = {
      title: link_title || link_name || name,
      description: link_description,
      isThumbnailHidden: hide_image,
      url,
    };

    if (content_type && isMicrosoftOfficeType(content_type)) {
      return <ViewMicrosoftFile url={url} className="rounded-lg" />;
    }

    return (
      <ExternalLinkCard
        link={link}
        isThumbnailHidden={hide_image}
        {...restProps}
        disabled={galleryMode}
        isURLHidden
        isAttachment
        fileAssetId={file_asset_id}
      />
    );
  };

  const renderDocument = () => {
    return (
      <div className="flex flex-1 flex-grow bg-white md:border-2 md:border-bedrock-light-gray rounded-lg justify-center overflow-y-auto">
        <div className="max-w-[680px] w-full py-4 md:p-8">
          {DocumentViewerFactory(content)()}
        </div>
      </div>
    )
  };

  const contentType = content_type;

  if (contentType === 'video' && isEmbeddableVideo(content)) {
    return renderEmbeddableVideo();
  } else if (contentType === 'pdf') {
    return renderPDF();
  } else if (contentType === 'external_link') {
    return renderExternalLink();
  } else if (contentType === 'image') {
    return renderImage();
  } else if (contentType === 'file') {
    return renderFile();
  } else if (contentType === 'journey_document') {
    return renderDocument();
  }

  return renderIntegration();
};

export default JourneyViewContent;
